import React, { useEffect, useState } from "react";
import { Avatar, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { ProductionTypeCategoryList } from "../../utils";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import dayjs from "dayjs";

const Row = ({ intProductTypeID, flavorsList = [], headers = [], categoryTotals = {}, reportType = "" }) => {
    const [open, setOpen] = React.useState(false);
    const { product_type_name: productTypeName } = ProductionTypeCategoryList?.find((j) => j?.intProductTypeID === intProductTypeID);

    const filteredFlavorsList = flavorsList?.reduce((result, flv) => {
        const hasData = Object.values(flv.reports).some(report => report[reportType]);
        if (hasData) return [...result, flv];
        else return result;
    }, []);

    if (filteredFlavorsList?.length === 0) {
        return null;
    }

    return (
        <React.Fragment>
            <TableRow className="even:bg-[#FFF8FA]">
                <TableCell align="left" className="border-[1px] p-[9px] pl-4 font-medium">
                    {productTypeName} {<span style={{ width: 20, margin: '0 5px 0 auto' }}>({filteredFlavorsList.length})</span>}
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                {headers.map(({ id }, index) => <TableCell key={index} align="center" className="border-[1px] p-1 font-medium">{categoryTotals[id]}</TableCell>)}
                <TableCell align="center" className="border-[1px] p-1 font-medium">{Object.values(categoryTotals).reduce((a, b) => a + b, 0)}</TableCell>
            </TableRow>
            {open && filteredFlavorsList.map((flavor, index) => (
                <TableRow key={index} className="even:bg-[#FFF8FA]">
                    <TableCell align="left" className="border-[1px] p-[9px]">
                        <span className="flex items-center">
                            {flavor?.imageUrl ? <Avatar
                                title={flavor?.flavor_name}
                                sx={{
                                    width: 30, height: 30
                                }}
                                style={{ marginRight: "10px" }}
                                alt=""
                                src={flavor?.imageUrl}
                            />
                                : <Avatar
                                    title={flavor?.flavor_name}
                                    sx={{
                                        width: 28, height: 28
                                    }}
                                    style={{
                                        background: "#fff",
                                        border: "2px solid grey",
                                        marginRight: "10px"
                                    }}
                                > </Avatar>}
                            <span title={flavor?.flavor_name} className="flavor-text">{flavor?.flavor_name}</span>
                        </span>
                    </TableCell>
                    {headers.map(({ id }, index) => <TableCell key={index} align="center" className="border-[1px] p-1 font-medium">{flavor?.reports?.[id]?.[reportType]}</TableCell>)}
                    <TableCell align="center" className="border-[1px] p-1 font-medium">{Object.values(flavor?.reports)?.reduce((a, b) => a + b[reportType], 0)}</TableCell>
                </TableRow>
            ))}
        </React.Fragment>
    );
}

const TotalsRow = ({ headers, grandTotals }) => {
    grandTotals = grandTotals.reduce((acc, cur, i) => {
        for (const key in cur) {
            if (cur.hasOwnProperty(key))
                acc[key] = (acc[key] || 0) + cur[key];
        }
        return acc;
    }, {});
    const sumOfTotals = Object.values(grandTotals)?.reduce((result, curValue) => result + curValue, 0);

    return (
        <TableRow className="bg-[#8CD2F4]">
            <TableCell align="center" className="border-[1px] p-[12px] font-bold">Total</TableCell>
            {headers.map(({ id }, index) => <TableCell key={index} align="center" className="border-[1px] p-1 font-bold">{grandTotals?.[id]}</TableCell>)}
            <TableCell align="center" className="border-[1px] p-[12px] font-bold">{sumOfTotals}</TableCell>
        </TableRow>
    )
}

export const ReportsTable = ({ type = "", headers = [], data = [], reportType = "", groupBy = "" }) => {
    const grandTotals = [];
    const [fadeOut, setFadeOut] = useState(false);

    useEffect(() => {
        setFadeOut(true);
        setTimeout(() => setFadeOut(false), 500);
    }, [reportType]);

    return (
        <div className="max-w-full overflow-x-auto">
            <Table className={`${fadeOut && "animate-tableFade"}`} sx={{ minWidth: 650 }} aria-label="reports table">
                <TableHead>
                    <TableRow className="bg-[#484848]">
                        <TableCell align="left" className="text-white p-[8px] pl-4 border-[1px]">Category & Flavor</TableCell>
                        {headers.map(({ label, date }, index) =>
                            <TableCell key={index} align="center" className="text-white font-bold p-[8px] border-[1px]">
                                {type !== 'weather' ?
                                    (groupBy === "WEEKLY" ? dayjs(date).format('D MMM') : label)
                                    : <span className="flex justify-center items-center">
                                        <img src="../../assets/icons/temperature-white-icon.png" alt="temp." className="pr-2 h-4" />
                                        {label}&deg;F
                                    </span>
                                }
                            </TableCell>
                        )}
                        <TableCell align="center" className="text-white font-medium p-[8px] border-[1px]">Total</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody className="">
                    {data?.map(({ intProductTypeID, flavorsList }, index) => {
                        const categoryTotals = flavorsList.reduce((acc, cur, i) => {
                            const reports = Object.entries(cur.reports);
                            reports.forEach(([id, values], i) => {
                                if (acc[id]) acc[id] += values?.[reportType];
                                else acc[id] = values?.[reportType];
                            });
                            return acc;
                        }, {});
                        grandTotals.push(categoryTotals);
                        return <Row key={index} intProductTypeID={intProductTypeID} flavorsList={flavorsList} headers={headers} categoryTotals={categoryTotals} reportType={reportType} />
                    })}
                    <TotalsRow headers={headers} grandTotals={grandTotals} />
                </TableBody>
            </Table >
        </div>
    )
};